import { AlertTriangle, ArrowDown, ArrowUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VersionBadge } from "@/components/ui/documents/version-badge";
import { FileTypeIcon } from "@/components/ui/documents/file-type-icon";
import { SegmentedControl, type SegmentedControlOption } from "@/components/ui/segmented-control";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { formatFileSize } from "@/utils/formatFileSize";
import type { DocumentVersioningTranslations } from "@/lib/i18n/types/documents-sections/versioning";
import type { AddAsVersionMode } from "@/stores/documents/repository";
import type { StagedVersionItem } from "../../types/addVersions";

export interface StagedVersionRowProps {
  item: StagedVersionItem;
  copy: DocumentVersioningTranslations["addNewVersion"];
  /** First and last version number this row will take. */
  firstVersion: number;
  lastVersion: number;
  isFirst: boolean;
  isLast: boolean;
  disabled: boolean;
  /** The target document's filename, for the mismatch hint on uploads. */
  targetFilename: string;
  /** Why Move is unavailable for this row, or null when it is. */
  moveDisabledReason: string | null;
  /** Set when a move brings the document in from another project. */
  crossProjectName: string | null;
  /** The source project's name, for existing documents. */
  sourceProjectName: string | null;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
  onModeChange: (mode: AddAsVersionMode) => void;
}

/**
 * One row of the "Add new version" list: an uploaded file, or an existing
 * document moved or copied in. The version badge shows the number(s) the row
 * will take — a moved lineage takes one per version it brings.
 */
export function StagedVersionRow({
  item,
  copy,
  firstVersion,
  lastVersion,
  isFirst,
  isLast,
  disabled,
  targetFilename,
  moveDisabledReason,
  crossProjectName,
  sourceProjectName,
  onMove,
  onRemove,
  onModeChange,
}: StagedVersionRowProps) {
  const name = item.kind === "upload" ? item.file.name : item.document.filename;
  const mismatched =
    item.kind === "upload" &&
    item.file.name.trim().toLowerCase() !== targetFilename.trim().toLowerCase();
  const waitingForHistory =
    item.kind === "existing" && item.mode === "move" && item.lineage === null;

  const modeOptions: Array<SegmentedControlOption<AddAsVersionMode>> = [
    {
      value: "move",
      label: copy.modeMove,
      disabled: moveDisabledReason !== null,
      title: moveDisabledReason ?? undefined,
    },
    { value: "copy", label: copy.modeCopy },
  ];

  let detail: string;
  if (item.kind === "upload") {
    detail = formatFileSize(item.file.size);
  } else if (item.mode === "copy") {
    detail = copy.copyHint;
  } else {
    const live = item.lineage?.liveCount ?? 1;
    detail = live > 1 ? copy.moveHintMany.replace("{count}", String(live)) : copy.moveHintSingle;
  }

  return (
    <li className="flex items-start gap-3 rounded-md border p-2">
      {waitingForHistory ? (
        <Skeleton
          className="mt-0.5 h-6 w-8 shrink-0 rounded-full"
          aria-label={copy.checkingHistory}
        />
      ) : (
        // Widen past the atom's fixed circle so v10+ and ranges fit.
        <VersionBadge
          version={firstVersion}
          lastVersion={lastVersion}
          className="mt-0.5 w-auto min-w-6 shrink-0 px-1.5"
        />
      )}

      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center gap-2">
          {item.kind === "existing" && <FileTypeIcon filename={name} />}
          <span className="truncate text-sm font-medium">{name}</span>
          {item.kind === "existing" && !item.document.isCurrentVersion && (
            <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
              {copy.versionChip.replace("{version}", String(item.document.version ?? 1))}
            </span>
          )}
          {mismatched && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex shrink-0 items-center gap-1 text-xs text-amber-600 dark:text-amber-500">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {copy.mismatchBadge}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {copy.mismatchTooltip.replace("{existing}", targetFilename)}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          {item.kind === "existing" && sourceProjectName && (
            <>{copy.fromProject.replace("{project}", sourceProjectName)} · </>
          )}
          {detail}
          {isLast && ` · ${copy.becomesCurrent}`}
        </p>

        {item.kind === "existing" && item.lineageFailed && item.mode === "move" && (
          <p className="text-xs text-destructive">{copy.historyFailed}</p>
        )}
        {crossProjectName && (
          <p className="flex items-start gap-1 text-xs text-amber-600 dark:text-amber-500">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {copy.crossProjectMoveWarning.replace("{project}", crossProjectName)}
          </p>
        )}
      </div>

      {item.kind === "existing" && (
        <SegmentedControl
          value={item.mode}
          onValueChange={onModeChange}
          options={modeOptions}
          disabled={disabled}
          aria-label={copy.modeLabel}
          className="h-8 shrink-0"
        />
      )}

      <div className="flex shrink-0 items-center">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label={copy.moveUp}
          disabled={disabled || isFirst}
          onClick={() => onMove(-1)}
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          aria-label={copy.moveDown}
          disabled={disabled || isLast}
          onClick={() => onMove(1)}
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
          aria-label={copy.removeFile}
          disabled={disabled}
          onClick={onRemove}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}
